import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Printer, Download } from "lucide-react";
import { useApp, formatMoney } from "@/lib/app-context";
import { monthLabel, fmtDate, balanceLabel, exportYearExcel } from "@/lib/sheet-utils";
import { YearStatement } from "@/components/YearStatement";
import { Button } from "@/components/ui/button";
import { PaidToggle } from "@/components/PaidToggle";
import { SettleButton } from "@/components/SettleButton";

const Stat = ({ label, value, tone = "", testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-xs font-semibold text-slate-500">{label}</div>
    <div className={`font-mono text-xl sm:text-2xl font-extrabold mt-1 ${tone}`} data-testid={testId}>{value}</div>
  </div>
);
const TH = ({ children, right }) => (
  <th className={`bg-slate-100 text-slate-600 font-bold text-[11px] uppercase tracking-wider px-3 py-3 border-b-2 border-slate-300 whitespace-nowrap ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Num = ({ children, className = "" }) => <td className={`px-3 py-2 text-right font-mono whitespace-nowrap ${className}`}>{children}</td>;

function MonthsTable({ months, m }) {
  return (
    <div className="w-full overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="year-table">
      <table className="w-full text-sm">
        <thead><tr><TH>Month</TH><TH right>Days</TH><TH right>Earned</TH><TH right>B/F</TH><TH right>Bonus</TH><TH right>Advance</TH><TH right>Deduct</TH><TH right>Payable</TH><TH right>Paid</TH><TH right>Balance c/f</TH><TH>Status</TH><TH>Note</TH></tr></thead>
        <tbody>
          {months.map((r) => (
            <tr key={r.id} className="border-b border-slate-100" data-testid={`year-row-${r.month}`}>
              <td className="px-3 py-2 font-semibold whitespace-nowrap">{monthLabel(r.month)}</td>
              <Num>{r.days}</Num><Num>{m(r.earned)}</Num><Num>{m(r.brought_forward)}</Num><Num className="text-emerald-700">{m(r.bonus || 0)}{r.bonus_type && <div className="text-[10px] font-semibold text-slate-500">{r.bonus_type}</div>}</Num><Num>{m(r.advance)}</Num>
              <Num>{m(r.deduct)}</Num><Num className="font-bold">{m(r.payable)}</Num><Num>{m(r.paid_amount)}</Num>
              <Num className={`font-bold ${r.balance < 0 ? "text-red-600" : ""}`}>{m(r.balance)}</Num>
              <td className="px-3 py-2"><PaidToggle row={r} onToggle={() => {}} testId={`year-status-${r.month}`} /></td>
              <td className="px-3 py-2 text-slate-600 min-w-[140px]">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function YearSummary() {
  const { id } = useParams();
  const { api } = useApp();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setFailed(false);
      setData(await api("get", `/staff/${id}/summary?year=${year}`));
    } catch {
      setFailed(true);
    }
  }, [api, id, year]);
  useEffect(() => { load(); }, [load]);

  if (failed) return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="year-error">
      <p className="text-slate-600">This staff member could not be found.</p>
      <Link to="/staff" className="inline-block mt-3 text-teal-700 font-semibold underline" data-testid="year-error-back">Back to staff list</Link>
    </div>
  );
  if (!data) return <div className="text-slate-500 text-sm" data-testid="year-loading">Loading…</div>;
  const { staff, totals, months, current_balance, current_month } = data;
  const currency = staff.currency || "INR";
  const m = (n) => formatMoney(n, currency);
  return (
    <div className="space-y-6 fade-up" data-testid="year-summary-page">
      <Link to="/staff" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-teal-700" data-testid="back-to-staff"><ArrowLeft size={16} />Staff list</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="year-staff-name">{staff.name}</h1>
          <p className="text-sm text-slate-600 mt-1">Agent: {staff.agent_name || "—"} · Joined {fmtDate(staff.joining_date) || "—"} · Salary {m(staff.base_salary)} · {currency}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SettleButton staffId={staff.id} name={staff.name} balance={current_balance} month={current_month} currency={currency} onDone={load} />
          <Button variant="outline" className="border-slate-300" onClick={() => window.print()} disabled={!months.length} data-testid="print-statement-button"><Printer size={16} />Print</Button>
          <Button variant="outline" className="border-slate-300" onClick={() => exportYearExcel(data)} disabled={!months.length} data-testid="download-statement-button"><Download size={16} />Excel</Button>
        <div className="flex items-center gap-2">
          <button onClick={() => setYear(year - 1)} className="p-1.5 rounded-lg hover:bg-white border border-transparent hover:border-slate-200" data-testid="prev-year-button"><ChevronLeft size={20} /></button>
          <div className="font-heading text-2xl font-extrabold w-20 text-center" data-testid="year-title">{year}</div>
          <button onClick={() => setYear(year + 1)} className="p-1.5 rounded-lg hover:bg-white border border-transparent hover:border-slate-200" data-testid="next-year-button"><ChevronRight size={20} /></button>
        </div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label={`Total earned ${year}`} value={m(totals.earned)} testId="year-total-earned" />
        <Stat label="Bonus" value={m(totals.bonus || 0)} tone="text-emerald-700" testId="year-total-bonus" />
        <Stat label="Advance + deduct" value={m(totals.advance + totals.deduct)} tone="text-red-600" testId="year-total-cuts" />
        <Stat label={`Total paid ${year}`} value={m(totals.paid_amount)} tone="text-emerald-700" testId="year-total-paid" />
        <Stat label={`Current balance${current_month ? ` (${monthLabel(current_month)})` : ""} · ${balanceLabel(current_balance)}`} value={m(current_balance)} tone={current_balance < 0 ? "text-red-600" : current_balance === 0 ? "text-emerald-700" : "text-amber-600"} testId="year-current-balance" />
      </div>
      {months.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="year-empty">No salary months recorded in {year}.</div>
      ) : <MonthsTable months={months} m={m} />}
      <YearStatement data={data} currency={currency} />
    </div>
  );
}
